"use client";

import React from "react";
import {
  Box,
  Grid,
  Card,
  CardBody,
  Text,
  Badge,
  HStack,
  VStack,
  Flex,
  Select,
  Tabs,
  TabList,
  Tab,
  Stat,
  StatLabel,
  StatNumber,
  useToast,
  Spinner,
  Avatar,
  IconButton,
  Icon,
} from "@chakra-ui/react";
import { EditIcon, DeleteIcon } from "@chakra-ui/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRobot, faPhone } from "@fortawesome/free-solid-svg-icons";
import { FiMessageSquare } from "react-icons/fi";
import { motion } from "framer-motion";
import AgentsPage from "./AgentsPage";
import SMSPhoneNumbers from "./sms-agents/SMSPhoneNumbers";
import SMSKnowledgeBase from "./sms-agents/SMSKnowledgeBase";
import { CreateAgent } from "./sms-agents/CreateAgent";
import { smsAgentService, type SMSAgent } from "../sms-services/agentService";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import { useStableCredits } from "../hooks/useStableCredits";
import { supabase } from "../supabaseClient";
import { getDisplayPhonePrettyFromFields } from "../voice-services/voicePhoneDisplay";

const MotionBox = motion(Box);

type LoadingFlags = {
  agents: boolean;
  phoneNumbers: boolean;
  metrics: boolean;
};

const SMSAgents: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const { isAdmin, currentClientId, clients, userProfile } = useAuth();
  const credits = useStableCredits();

  const [selectedAgentType, setSelectedAgentType] = React.useState<string>(
    () => {
      return localStorage.getItem("currentAgentType") || "SMS";
    },
  );

  const [selectedSMSTab, setSelectedSMSTab] = React.useState<string>(() => {
    return localStorage.getItem("currentAgentTab") || "agents";
  });

  const [smsAgents, setSmsAgents] = React.useState<SMSAgent[]>([]);
  const [phoneNumbersByAgent, setPhoneNumbersByAgent] = React.useState<
    Record<string, string[]>
  >({});

  const [stats, setStats] = React.useState({
    totalAgents: 0,
    activeAgents: 0,
    totalInteractions: 0,
    avgConvLength: 0, // still accepted from backend, not shown on cards
  });

  // ✅ Standard: granular loading + keep UI rendered
  const [loading, setLoading] = React.useState<LoadingFlags>({
    agents: true,
    phoneNumbers: true,
    metrics: true,
  });

  const abortControllerRef = React.useRef<AbortController | null>(null);

  const companyName = React.useMemo(() => {
    const currentClient = clients.find((c) => c.id === currentClientId);
    return isAdmin && currentClient ? currentClient.company_name : userProfile?.company_name;
  }, [isAdmin, currentClientId, clients, userProfile]);

  // Keep current tab in sync (existing behavior)
  React.useEffect(() => {
    const savedTab = localStorage.getItem("currentAgentTab");
    if (savedTab) setSelectedSMSTab(savedTab);
  }, []);

  React.useEffect(() => {
    if (location.pathname.startsWith("/sms-agents")) {
      setSelectedAgentType("SMS");
      localStorage.setItem("currentAgentType", "SMS");
    }
  }, [location.pathname]);

  // Tenant switch: abort inflight + clear UI (no caching)
  React.useEffect(() => {
    if (abortControllerRef.current) abortControllerRef.current.abort();

    setSmsAgents([]);
    setPhoneNumbersByAgent({});
    setStats({
      totalAgents: 0,
      activeAgents: 0,
      totalInteractions: 0,
      avgConvLength: 0,
    });

    setLoading({
      agents: true,
      phoneNumbers: true,
      metrics: true,
    });
  }, [companyName]);

  const fetchData = React.useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    // ✅ Standard: revalidate without blanking the whole page
    setLoading((prev) => ({
      ...prev,
      agents: true,
      phoneNumbers: true,
      metrics: true,
    }));

    const defaultStats = {
      totalAgents: 0,
      activeAgents: 0,
      totalInteractions: 0,
      avgConvLength: 0,
    };

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      // Fetch agents (progressive)
      void smsAgentService
        .list(companyName)
        .then((freshAgents) => {
          if (signal.aborted) return;

          const map = new Map<string, SMSAgent>();
          freshAgents.forEach((a: SMSAgent) => map.set(a.agent_id, a));
          setSmsAgents(Array.from(map.values()));
        })
        .catch((error: any) => {
          if (error?.name === "AbortError") return;
          console.error("Error loading SMS agents:", error);
          toast({
            title: "Error loading SMS agents",
            status: "error",
            duration: 3000,
          });
        })
        .finally(() => {
          if (!signal.aborted) setLoading((p) => ({ ...p, agents: false }));
        });

      // Fetch SMS phone numbers (progressive)
      void (async () => {
        try {
          const response = await fetch(
            `${process.env.REACT_APP_SERVER_URL}/api/sms/numbers`,
            {
              headers: {
                Authorization: `Bearer ${currentClientId}`,
              },
              signal,
            },
          );

          if (signal.aborted) return;

          const freshNumbers = response.ok ? await response.json() : [];
          const agentPhoneMap: Record<string, string[]> = {};

          // We store as { [agent_id]: [pretty numbers] }
          (freshNumbers || []).forEach((row: any) => {
            const agentId = row.agent_id;
            if (!agentId) return;

            const canonical = (
              row.phone_number ||
              row.number ||
              row.phone ||
              ""
            ).trim();
            const prettyRaw = (row.phone_number_pretty || "").trim();
            const display = getDisplayPhonePrettyFromFields({
              phone_number: canonical || prettyRaw,
              phone_number_pretty: prettyRaw || null,
            });
            if (!display) return;
            (agentPhoneMap[agentId] ??= []).push(display);
          });

          setPhoneNumbersByAgent(agentPhoneMap);
        } catch (error: any) {
          if (error?.name === "AbortError") return;
          console.error("Error loading SMS phone numbers:", error);
        } finally {
          if (!signal.aborted)
            setLoading((p) => ({ ...p, phoneNumbers: false }));
        }
      })();

      // Fetch top metrics (progressive)
      void (async () => {
        try {
          const url = new URL(
            `${process.env.REACT_APP_SERVER_URL}/api/sms-agents/stats`,
          );
          if (companyName) url.searchParams.append("company_name", companyName);

          const response = await fetch(url.toString(), {
            headers: {
              Authorization: `Bearer ${currentClientId}`,
            },
            signal,
          });

          if (signal.aborted) return;

          if (response.ok) {
            const freshStats = await response.json();
            if (!signal.aborted) setStats(freshStats);
          } else {
            if (!signal.aborted) setStats(defaultStats);
          }
        } catch (error: any) {
          if (error?.name === "AbortError") return;
          console.error("Error loading SMS stats:", error);
          if (!signal.aborted) setStats(defaultStats);
        } finally {
          if (!signal.aborted) setLoading((p) => ({ ...p, metrics: false }));
        }
      })();

      if (!session) {
        // still keep page usable; metrics endpoints use currentClientId anyway
        console.warn("No active Supabase session (SMS).");
      }
    } catch (error: any) {
      if (error?.name === "AbortError") return;
      console.error("Error loading SMS page:", error);
      toast({
        title: "Error loading SMS agents",
        status: "error",
        duration: 3000,
      });

      if (!signal.aborted) {
        setLoading({
          agents: false,
          phoneNumbers: false,
          metrics: false,
        });
      }
    }
  }, [companyName, currentClientId, toast]);

  React.useEffect(() => {
    fetchData();
    return () => abortControllerRef.current?.abort();
  }, [fetchData]);

  // keep stats fresh after admin/client changes (avoids storms; progressive)
  React.useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, currentClientId]);

  const handleAgentTypeChange = (type: string) => {
    setSelectedAgentType(type);
    localStorage.setItem("currentAgentType", type);

    if (type === "All") navigate("/all-agents");
    else if (type === "Voice") navigate("/voice-agents");
    else if (type === "SMS") navigate("/sms-agents");
    else if (type === "Email") navigate("/email-agents");
    else if (type === "WhatsApp") navigate("/whatsapp-agents");
  };

  const handleDeleteAgent = async (agentId: string) => {
    try {
      await smsAgentService.delete(agentId);
      toast({
        title: "SMS Agent deleted successfully",
        status: "success",
        duration: 3000,
      });
      fetchData();
    } catch (error) {
      toast({
        title: "Error deleting SMS agent",
        status: "error",
        duration: 3000,
      });
    }
  };

  const getTabIndex = () => {
    switch (selectedSMSTab) {
      case "agents":
        return 0;
      case "phone-numbers":
        return 1;
      case "knowledge-base":
        return 2;
      default:
        return 0;
    }
  };

  const handleTabIndexChange = (index: number) => {
    const tabs = ["agents", "phone-numbers", "knowledge-base"];
    const newTab = tabs[index];
    setSelectedSMSTab(newTab);
    localStorage.setItem("currentAgentTab", newTab);
    window.dispatchEvent(new CustomEvent("agentTabChange", { detail: newTab }));
  };

  if (selectedAgentType === "All") return <AgentsPage />;

  if (selectedAgentType === "SMS") {
    if (selectedSMSTab === "phone-numbers") return <SMSPhoneNumbers />;
    if (selectedSMSTab === "knowledge-base") return <SMSKnowledgeBase />;
  }

  const disabled = credits !== null && credits < 0.01;

  return (
    <Box p={6} bg="white" minH="100vh">
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Tabs
            index={getTabIndex()}
            onChange={handleTabIndexChange}
            variant="solid-rounded"
            colorScheme="blackAlpha"
          >
            <TabList
              bg="gray.100"
              p={1}
              borderRadius="lg"
              border="1px solid"
              borderColor="gray.300"
            >
              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg: selectedSMSTab === "agents" ? "white" : "gray.200",
                }}
              >
                💬 SMS Agents
              </Tab>

              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg: selectedSMSTab === "phone-numbers" ? "white" : "gray.200",
                }}
              >
                📞 SMS Phone Numbers
              </Tab>

              <Tab
                fontWeight="bold"
                fontSize="md"
                color="black"
                _selected={{ bg: "white", color: "black", boxShadow: "sm" }}
                _hover={{
                  bg:
                    selectedSMSTab === "knowledge-base" ? "white" : "gray.200",
                }}
              >
                📚 Knowledge Base
              </Tab>
            </TabList>
          </Tabs>
        </HStack>

        <HStack spacing={2}>
          <MotionBox
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            transition="0.15s ease"
          >
            <Select
              value={selectedAgentType}
              onChange={(e) => handleAgentTypeChange(e.target.value)}
              height="48px"
              bg="white"
              border="2px solid"
              borderColor="gray.200"
              borderRadius="12px"
              color="gray.800"
              fontWeight="semibold"
              fontSize="sm"
              pl="12px"
              pr="36px"
              cursor="pointer"
              shadow="sm"
              transition="all 0.2s ease"
              _hover={{ borderColor: "blue.400", shadow: "md" }}
              _focus={{
                borderColor: "blue.600",
                boxShadow: "0 0 0 3px rgba(99, 102, 241, 0.15)",
              }}
              isDisabled={disabled}
              opacity={disabled ? 0.6 : 1}
              title={
                disabled
                  ? "Insufficient balance. Please add balance to view agents."
                  : undefined
              }
            >
              <option value="All">🤖 All Agents</option>
              <option value="Voice">📞 Voice Agents</option>
              <option value="SMS">💬 SMS Agents</option>
              {process.env.REACT_APP_IS_MAIN_APP === "true" && (
                <option value="Email">📧 Email Agents</option>
              )}
              <option value="WhatsApp">📱 WhatsApp Agents</option>
            </Select>
          </MotionBox>

          <CreateAgent
            onAgentCreated={fetchData}
            buttonProps={{ variant: "brandGradient", isDisabled: disabled }}
          />
        </HStack>
      </Flex>

      {/* Top metrics (kept lightweight; no per-agent card stats anymore) */}
      {smsAgents.length > 0 && (
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
          gap={4}
          mb={6}
        >
          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Agents</StatLabel>
                <StatNumber color="black">
                  {loading.metrics ? <Spinner size="sm" /> : stats.totalAgents}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Active Agents</StatLabel>
                <StatNumber color="green.500">
                  {loading.metrics ? <Spinner size="sm" /> : stats.activeAgents}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>

          <Card _hover={{ shadow: "lg" }} transition="all 0.2s ease">
            <CardBody>
              <Stat>
                <StatLabel color="gray.600">Total Interactions</StatLabel>
                <StatNumber color="black">
                  {loading.metrics ? (
                    <Spinner size="sm" />
                  ) : (
                    stats.totalInteractions
                  )}
                </StatNumber>
              </Stat>
            </CardBody>
          </Card>
        </Grid>
      )}

      {/* Empty state (no full-screen gate; show spinner in the empty box when loading agents) */}
      {smsAgents.length === 0 ? (
        <Flex
          direction="column"
          align="center"
          justify="center"
          h="500px"
          minH="calc(100vh - 80px)"
          bg="gray.50"
          borderRadius="md"
          border="1px solid"
          borderColor="gray.200"
        >
          {loading.agents ? (
            <Spinner size="xl" color="blue.500" />
          ) : (
            <>
              <FontAwesomeIcon icon={faRobot} color="gray" size="3x" />
              <Text fontSize="lg" fontWeight="medium" color="gray.600" mt={4}>
                No SMS Agents Found!
              </Text>
              <Text fontSize="sm" color="gray.500" mt={2}>
                Create an SMS agent to get started.
              </Text>
              <Box mt={6}>
                <CreateAgent
                  onAgentCreated={fetchData}
                  buttonProps={{
                    mt: 6,
                    colorScheme: "blue",
                    bg: "blue.800",
                    _hover: { bg: "blue.900" },
                    isDisabled: disabled,
                  }}
                />
              </Box>
            </>
          )}
        </Flex>
      ) : (
        <Grid templateColumns="repeat(auto-fit, minmax(520px, 1fr))" gap={6}>
          {smsAgents.map((agent) => {
            const agentPhones = phoneNumbersByAgent[agent.agent_id] || [];
            const bluePhone = agentPhones[0];
            const hasPhone = agentPhones.length > 0;

            const agentTypeLabel = "Single-Prompt";
            const agentTypeColor = "purple";

            const cardDisabled = disabled;

            return (
              <Card
                role="button"
                tabIndex={cardDisabled ? -1 : 0}
                aria-disabled={cardDisabled}
                key={agent.agent_id}
                borderLeft="4px"
                borderLeftColor="blue.500"
                cursor={cardDisabled ? "not-allowed" : "pointer"}
                opacity={cardDisabled ? 0.55 : 1}
                onClick={() => {
                  if (cardDisabled) return;
                  navigate(`/sms-agents/${agent.agent_id}`);
                }}
                _hover={cardDisabled ? {} : { shadow: "md" }}
                transition="all 0.2s"
                minH="250px"
                display="flex"
                flexDirection="column"
                title={
                  cardDisabled
                    ? "Insufficient balance to view agent details"
                    : undefined
                }
              >
                <CardBody display="flex" flexDirection="column" p={5}>
                  <VStack align="stretch" spacing={3} flex={1}>
                    {/* Header */}
                    <Flex justify="space-between" align="start" gap={3}>
                      <HStack align="start" spacing={3} flex={1} minW={0}>
                        {/* Avatar (bigger + blue outline) */}
                        <Box
                          w="52px"
                          h="52px"
                          borderRadius="full"
                          border="2px solid"
                          borderColor="blue.500"
                          overflow="hidden"
                          flexShrink={0}
                          bg="white"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Avatar
                            size="sm"
                            bg="blue.500"
                            icon={
                              <Icon
                                as={FiMessageSquare}
                                color="white"
                                fontSize="1.05rem"
                              />
                            }
                          />
                        </Box>

                        <VStack align="start" spacing={1} flex={1} minW={0}>
                          <Text
                            fontSize="lg"
                            fontWeight="600"
                            color="black"
                            noOfLines={2}
                            lineHeight="1.2"
                          >
                            {agent.name || "Unnamed Agent"}
                          </Text>

                          <HStack spacing={2} flexWrap="wrap">
                            <Badge colorScheme="blue" fontSize="xs">
                              SMS
                            </Badge>

                            <Badge
                              colorScheme={hasPhone ? "green" : "yellow"}
                              fontSize="xs"
                            >
                              {hasPhone ? "ACTIVE" : "PAUSED"}
                            </Badge>
                          </HStack>
                        </VStack>
                      </HStack>

                      {/* Actions */}
                      <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          aria-label="Edit agent"
                          icon={<EditIcon />}
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            navigate(`/sms-agents/${agent.agent_id}`)
                          }
                          isDisabled={cardDisabled}
                          title={
                            cardDisabled
                              ? "Insufficient balance to edit agent"
                              : "Edit"
                          }
                        />
                        <IconButton
                          aria-label="Delete agent"
                          icon={<DeleteIcon />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteAgent(agent.agent_id)}
                          title="Delete"
                        />
                      </HStack>
                    </Flex>

                    {/* Details */}
                    <VStack
                      align="stretch"
                      spacing={2}
                      pt={2}
                      borderTop="1px solid"
                      borderColor="gray.100"
                    >
                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color="gray.600">
                          Phone Number:
                        </Text>

                        {loading.phoneNumbers ? (
                          <Spinner size="xs" />
                        ) : hasPhone ? (
                          <Badge
                            colorScheme="green"
                            fontSize="xs"
                            display="inline-flex"
                            alignItems="center"
                            gap={2}
                            px={2}
                            py={1}
                            borderRadius="md"
                          >
                            <FontAwesomeIcon icon={faPhone} />
                            <Text as="span">{bluePhone}</Text>
                          </Badge>
                        ) : (
                          <Text fontSize="sm" fontWeight="600" color="gray.800">
                            Not connected
                          </Text>
                        )}
                      </Flex>

                      <Flex justify="space-between" align="center">
                        <Text fontSize="sm" color="gray.600">
                          Agent Type:
                        </Text>

                        <Badge colorScheme={agentTypeColor} fontSize="xs">
                          {agentTypeLabel}
                        </Badge>
                      </Flex>
                    </VStack>

                    {/* Footer (Last updated bottom-right) */}
                    <Flex
                      pt={2}
                      mt="auto"
                      borderTop="1px solid"
                      borderColor="gray.100"
                      justify="flex-end"
                    >
                      <Text fontSize="xs" color="gray.500">
                        Last Updated:{" "}
                        {new Date(agent.updated_at).toLocaleString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        })}
                      </Text>
                    </Flex>
                  </VStack>
                </CardBody>
              </Card>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default SMSAgents;
